import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
interface HeroProps {
  onSearch?: (service: string, zip: string) => void;
}
export function Hero({
  onSearch
}: HeroProps) {
  const [service, setService] = useState('');
  const [zip, setZip] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && service && zip) {
      onSearch(service, zip);
    } else if (!service) {
      alert('Please select a service type');
    } else if (!zip) {
      alert('Please enter your zip code');
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden" data-id="element-304">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-305">
        <motion.div className="text-center max-w-4xl mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-306">
          <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-307">
            Find Trusted Local Pros.
            <br className="hidden md:block" data-id="element-308" /> Compare Free Quotes.
          </motion.h1>

          <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-309">
            Connect with vetted service professionals in your area. Get multiple
            quotes, compare prices, and hire with confidence.
          </motion.p>

          <motion.div variants={itemVariants} className="max-w-3xl mx-auto bg-white rounded-2xl p-2 md:p-3 shadow-xl" data-id="element-310">
            <form className="flex flex-col md:flex-row gap-3" onSubmit={handleSubmit} data-id="element-311">
              <div className="flex-1 relative" data-id="element-312">
                <label htmlFor="service-type" className="sr-only" data-id="element-313">
                  Select Service
                </label>
                <select id="service-type" value={service} onChange={e => setService(e.target.value)} className="w-full h-14 md:h-16 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent appearance-none" data-id="element-314">
                  <option value="" disabled data-id="element-315">
                    What do you need help with?
                  </option>
                  <optgroup label="Bathroom" data-id="element-316">
                    <option value="walk-in-showers" data-id="element-317">Walk-In Showers</option>
                    <option value="walk-in-tubs" data-id="element-318">Walk-In Tubs</option>
                    <option value="bathroom-remodel" data-id="element-319">Bathroom Remodel</option>
                  </optgroup>
                  <optgroup label="Kitchen" data-id="element-320">
                    <option value="kitchen-remodel" data-id="element-321">Kitchen Remodel</option>
                  </optgroup>
                  <optgroup label="Exterior" data-id="element-322">
                    <option value="roofing" data-id="element-323">Roofing</option>
                    <option value="siding" data-id="element-324">Siding</option>
                    <option value="window-replacement" data-id="element-325">
                      Window Replacement
                    </option>
                    <option value="doors" data-id="element-326">Doors</option>
                    <option value="garage-doors" data-id="element-327">Garage Doors</option>
                    <option value="gutter-guards" data-id="element-328">Gutter Guards</option>
                    <option value="fencing" data-id="element-329">Fencing</option>
                    <option value="painting" data-id="element-330">Painting</option>
                  </optgroup>
                  <optgroup label="Systems" data-id="element-331">
                    <option value="hvac" data-id="element-332">HVAC</option>
                    <option value="plumbing" data-id="element-333">Plumbing</option>
                    <option value="electrical" data-id="element-334">Electrical</option>
                    <option value="solar" data-id="element-335">Solar Panels</option>
                    <option value="insulation" data-id="element-336">Insulation</option>
                    <option value="home-security" data-id="element-337">Home Security</option>
                  </optgroup>
                  <optgroup label="Interior" data-id="element-338">
                    <option value="flooring" data-id="element-339">Flooring</option>
                    <option value="stairlifts" data-id="element-340">Stairlifts</option>
                  </optgroup>
                  <optgroup label="Outdoor & Other" data-id="element-341">
                    <option value="landscaping" data-id="element-342">Landscaping</option>
                    <option value="pest-control" data-id="element-343">Pest Control</option>
                    <option value="cleaning" data-id="element-344">Cleaning</option>
                    <option value="general" data-id="element-345">General Repair</option>
                  </optgroup>
                </select>
              </div>

              <div className="flex-1 relative" data-id="element-346">
                <label htmlFor="zip-code" className="sr-only" data-id="element-347">
                  Zip Code
                </label>
                <input type="text" id="zip-code" value={zip} onChange={e => setZip(e.target.value)} placeholder="Enter your zip code" className="w-full h-14 md:h-16 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-348" />
              </div>

              <button type="submit" className="h-14 md:h-16 px-8 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-white" data-id="element-349">
                Get Free Quotes
                <ArrowRightIcon className="w-5 h-5" data-id="element-350" />
              </button>
            </form>
          </motion.div>

          <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center gap-2 text-slate-300 text-sm font-medium" data-id="element-351">
            <ShieldCheckIcon className="w-5 h-5 text-amber-500" data-id="element-352" />
            <span data-id="element-353">Trusted by 50,000+ homeowners across the country</span>
          </motion.div>
        </motion.div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-354">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-slate-700/20 blur-3xl" data-id="element-355" />
        <div className="absolute top-[60%] -left-[10%] w-[40%] h-[40%] rounded-full bg-amber-500/10 blur-3xl" data-id="element-356" />
      </div>
    </section>;
}